import * as React from 'react';
import { cn } from '@shared';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export type DaySheetlyInputVariant = 'text' | 'number' | 'price';
export type DaySheetlyPriceLocale = 'pt-BR' | 'en-US';

export interface DaySheetlyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  variant?: DaySheetlyInputVariant;
  /** Native input type for text-like values such as date and datetime-local. */
  type?: React.HTMLInputTypeAttribute;
  value: string | number | null;
  onValueChange: (value: string | number | null) => void;
  label?: string;
  helperText?: string;
  error?: string;
  testId?: string;
  /** Locale used to display and mask price values while the user types. */
  priceLocale?: DaySheetlyPriceLocale;
}

const baseInputClasses =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Detect whether the last separator in the string is `,` (pt-BR / most of EU)
 * or `.` (en-US). Returns the locale hint so we know which character is the
 * decimal separator.
 */
function detectLocale(raw: string): 'comma' | 'dot' {
  const lastComma = raw.lastIndexOf(',');
  const lastDot = raw.lastIndexOf('.');
  if (lastComma === -1 && lastDot === -1) return 'dot';
  return lastComma > lastDot ? 'comma' : 'dot';
}

/**
 * Parse a user-entered price string into a number. Accepts both
 * `1.234,56` (pt-BR) and `1,234.56` (en-US). Returns `null` for empty / invalid.
 */
export function parsePrice(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const negative = trimmed.startsWith('-');
  const body = negative ? trimmed.slice(1) : trimmed;

  const locale = detectLocale(body);
  let normalized: string;
  if (locale === 'comma') {
    // pt-BR: dots are thousands separators, comma is decimal.
    normalized = body.replace(/\./g, '').replace(',', '.');
  } else {
    // en-US: commas are thousands separators, dot is decimal.
    normalized = body.replace(/,/g, '');
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return negative ? -parsed : parsed;
}

/**
 * Parse a currency mask where the last two digits are always cents.
 * For example, typing `3750` produces `37.50` regardless of display locale.
 */
export function parsePriceCents(raw: string): number | null {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;

  const parsed = Number(digits) / 100;
  if (!Number.isFinite(parsed)) return null;
  return trimmed.startsWith('-') ? -parsed : parsed;
}

/**
 * Format a price number back to a user-friendly string using the locale hint.
 */
export function formatPrice(value: number | null, locale: 'comma' | 'dot' = 'dot'): string {
  if (value === null || !Number.isFinite(value)) return '';
  const fixed = value.toFixed(2);
  if (locale === 'comma') {
    const [intPart, decPart] = fixed.split('.');
    const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${withThousands},${decPart}`;
  }
  const [intPart, decPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${withThousands}.${decPart}`;
}

/**
 * Parse a user-entered number string into a finite number, or `null` if the
 * string is empty or contains non-numeric characters.
 */
export function parseNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

const DaySheetlyInput = React.forwardRef<HTMLInputElement, DaySheetlyInputProps>(
  (
    {
      variant = 'text',
      type,
      value,
      onValueChange,
      label,
      helperText,
      error,
      testId,
      className,
      disabled,
      placeholder,
      priceLocale = 'pt-BR',
      id,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const helperId = helperText || error ? `${inputId}-helper` : undefined;

    const stringValue =
      variant === 'text'
        ? (value ?? '')
        : variant === 'number'
          ? value === null || value === undefined
            ? ''
            : String(value)
          : value === null || value === undefined
            ? ''
            : formatPrice(value as number, priceLocale === 'pt-BR' ? 'comma' : 'dot');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (variant === 'number') {
        onValueChange(parseNumber(raw));
        return;
      }
      onValueChange(raw);
    };

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange(parsePriceCents(e.target.value));
    };

    const inputType: React.HTMLInputTypeAttribute =
      type ?? (variant === 'number' ? 'number' : 'text');

    const inputMode: React.HTMLAttributes<HTMLInputElement>['inputMode'] =
      variant === 'number' ? 'numeric' : variant === 'price' ? 'decimal' : undefined;

    const describedBy = helperId;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <Label htmlFor={inputId} className={cn(disabled && 'opacity-70')}>
            {label}
          </Label>
        )}
        <Input
          ref={ref}
          id={inputId}
          type={inputType}
          inputMode={inputMode}
          value={stringValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={variant === 'price' ? handlePriceChange : handleChange}
          onBlur={onBlur}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedBy}
          data-testid={testId}
          className={cn(
            baseInputClasses,
            error && 'border-destructive focus-visible:ring-destructive',
            className,
          )}
          {...rest}
        />
        {(helperText || error) && (
          <p
            id={helperId}
            className={cn(
              'text-xs',
              error ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  },
);
DaySheetlyInput.displayName = 'DaySheetlyInput';

export { DaySheetlyInput };
